import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Picker, Toast, Card, Dialog } from 'antd-mobile';
import { t } from '../../../../i18n';
import { validateAccessCodeFormat } from '../utils/accessCodeUtils';
import { validateAccessCodeLocation } from '../utils/accessCodeLocationValidation';
import { AccessCode } from './AccessInfoDisplay';
import { createAccessCode, updateAccessCode } from '../../graphql/mutations/accessCodeMutations';
import { showError } from '../../../../utils/exceptionUtils';
import { clearAccessCodesCache } from '../hooks/useAccessCodes';
import { getLocalizedAccessCodeTypes, getLocalizedAccessCodeTypeName } from '../utils/accessCodeUtils';
import { DeliveryDetailPath } from '../../../../enums/DeliveryNavigatePathEnum';

type FormMode = 'add' | 'update';

interface AccessCodeFormViewProps {
    mode: FormMode;
    code?: AccessCode; // 更新模式下的现有Code信息
    addressId?: number;
    trackingNumber?: string;
    address?: string;
    addressLat?: number;
    addressLon?: number;
    orderId: number; // 订单ID（用于创建门禁码）
    onBack: () => void; // 返回回调
    onSuccess?: () => void; // 成功回调（可选，用于刷新数据）
    fromCodeList?: boolean; // 是否从 codeList 页面进入（用于决定返回目标）
    skipDistanceLimit?: boolean; // 是否跳过500米距离限制
}

const AccessCodeFormView: React.FC<AccessCodeFormViewProps> = ({
    mode,
    code,
    addressId,
    trackingNumber,
    address,
    addressLat,
    addressLon,
    orderId,
    onBack,
    onSuccess,
    fromCodeList = false,
    skipDistanceLimit = false
}) => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [typePickerVisible, setTypePickerVisible] = useState(false);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);


    // 初始化表单数据（更新模式）
    useEffect(() => {
        if (mode === 'update' && code) {
            // 更新模式下，新密码默认为空，不预填原密码
            form.setFieldsValue({
                code: ''
            });
        }
    }, [mode, code, form]);

    const handleSubmit = async () => {
        // 防止重复提交
        if (isSubmitting) {
            return;
        }

        try {
            setIsSubmitting(true);
            const values = await form.validateFields();

            // 密码格式校验
            const codeValidation = validateAccessCodeFormat(values.code, t);
            if (!codeValidation.valid) {
                Toast.show({
                    content: codeValidation.message || t('pages.delivery.orderDetail.invalidCodeFormat'),
                    position: 'bottom'
                });
                return;
            }

            // 更新模式：检查密码是否有变化
            if (mode === 'update') {
                const hasCodeChanged = values.code !== code?.code;
                
                if (!hasCodeChanged) {
                    Toast.show({
                        content: t('pages.delivery.orderDetail.noChanges'),
                        position: 'bottom'
                    });
                    return;
                }
            }

            // 位置校验（双重校验，防止用户在填写表单过程中移动位置）
            // 使用缓存位置，性能影响小
            const locationResult = await validateAccessCodeLocation({
                addressLat,
                addressLon,
                skipDistanceLimit,
                isFormSubmit: true
            });

            // 如果位置校验不通过（200米外），不提交
            if (!locationResult.isValid) {
                return;
            }

            // 提交数据的实际逻辑
            const submitData = async (driverLocation: { latitude: number; longitude: number } | null) => {
                if (mode === 'add') {
                    // 检查是否有 type
                    if (!values.type) {
                        Toast.show({
                            content: t('pages.delivery.orderDetail.typeRequired'),
                            position: 'bottom'
                        });
                        return;
                    }

                    try {
                        const result = await createAccessCode({
                            orderId: orderId,
                            codeType: values.type,
                            accessCode: values.code,
                            latitude: driverLocation?.latitude ?? null,
                            longitude: driverLocation?.longitude ?? null
                        });
                    
                        if (result.success) {
                            // 清除缓存，确保下次获取最新数据
                            clearAccessCodesCache(orderId);
                            
                            // 根据结果类型显示不同的提示信息
                            if (result.resultType === 'duplicate_handled') {
                                Toast.show({
                                    content: t('pages.delivery.orderDetail.addCodeDuplicateHandled'),
                                    position: 'bottom'
                                });
                            } else {
                                Toast.show({
                                    content: t('pages.delivery.orderDetail.addCodeSuccess'),
                                    position: 'bottom'
                                });
                            }
                            // 新增成功后，调用成功回调
                            // 如果提供了 onSuccess，使用它处理返回逻辑（地图模式）
                            // 否则根据 fromCodeList 决定返回目标（列表模式）
                            if (onSuccess) {
                                onSuccess();
                            } else {
                            // 列表模式：简单回退即可
                            // 1. 从订单详情页进入的新增：回退到订单详情页
                            // 2. 从 codeList 进入的新增：回退到 codeList，再返回就回退到订单详情页
                            navigate(-1);
                            }
                        } else {
                            Toast.show({
                                content: t('pages.delivery.orderDetail.addCodeFailed'),
                                position: 'bottom'
                            });
                        }
                    } catch (error) {
                        console.error('Failed to create access code:', error);
                        showError(error);
                    }
                } else {
                    // 更新模式
                    if (!code?.id) {
                        Toast.show({
                            content: t('pages.delivery.orderDetail.codeIdRequired'),
                            position: 'bottom'
                        });
                        return;
                    }

                    try {
                        const result = await updateAccessCode({
                            orderId: orderId ?? null, // 传递订单ID用于数据重复性校验
                            accessCode: {
                                id: code.id,
                                codeType: code.type, // 前端使用 type，后端使用 codeType
                                accessCode: values.code, // 新的门禁码值
                                status: code.status ?? 0
                            },
                            latitude: driverLocation?.latitude ?? null,
                            longitude: driverLocation?.longitude ?? null
                        });
                
                        if (result.success) {
                            // 清除缓存，确保下次获取最新数据
                            if (orderId) {
                                clearAccessCodesCache(orderId);
                            }
                            
                            // 根据结果类型显示不同的提示信息
                            if (result.resultType === 'duplicate_handled') {
                                Toast.show({
                                    content: t('pages.delivery.orderDetail.addCodeDuplicateHandled'),
                                    position: 'bottom'
                                });
                            } else {
                                Toast.show({
                                    content: t('pages.delivery.orderDetail.updateCodeSuccess'),
                                    position: 'bottom'
                                });
                            }
                            // 更新成功后，调用成功回调
                            // 如果提供了 onSuccess，使用它处理返回逻辑（地图模式）
                            // 否则根据 fromCodeList 决定返回目标（列表模式）
                            if (onSuccess) {
                                onSuccess();
                            } else {
                                // 列表模式：简单回退即可
                                // 1. 从订单详情页进入的更新：回退到订单详情页
                                // 2. 从 codeList 进入的更新：回退到 codeList，再返回就回退到订单详情页
                                navigate(-1);
                            }
                        } else {
                            Toast.show({
                                content: t('pages.delivery.orderDetail.updateCodeFailed'),
                                position: 'bottom'
                            });
                        }
                    } catch (error) {
                        console.error('Failed to update access code:', error);
                        showError(error);
                    }
                }
            };

            // 执行提交
            await submitData(locationResult.driverLocation);
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 更新模式下，如果没有code信息，显示错误
    if (mode === 'update' && !code) {
        return (
            <div className="flex flex-col h-full bg-gray-50">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">
                        {t('pages.delivery.orderDetail.codeNotFound')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-4">
                <Card className="mb-4">
                    <Form
                        form={form}
                        layout="vertical"
                        footer={
                            <Button
                                block
                                type="submit"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                {t('pages.delivery.orderDetail.confirm')}
                            </Button>
                        }
                    >
                        {/* Address (read-only) */}
                        <Form.Item
                            label={t('pages.delivery.orderDetail.address')}
                        >
                            <div className="text-gray-700 break-words">{address || '-'}</div>
                        </Form.Item>

                        {/* Type */}
                        {mode === 'add' ? (
                            // 新增模式：选择类型
                            <Form.Item
                                name="type"
                                label={t('pages.delivery.orderDetail.type')}
                                rules={[{ required: true, message: t('pages.delivery.orderDetail.typeRequired') }]}
                            >
                                <div
                                    className={`py-2 px-3 bg-white border border-gray-200 rounded cursor-pointer ${selectedType ? 'text-gray-900' : 'text-gray-400'}`}
                                    onClick={() => setTypePickerVisible(true)}
                                >
                                    {selectedType 
                                        ? getLocalizedAccessCodeTypeName(selectedType, t)
                                        : t('pages.delivery.orderDetail.selectType')}
                                </div>
                                <Picker
                                    columns={[getLocalizedAccessCodeTypes(t)]}
                                    visible={typePickerVisible}
                                    value={selectedType ? [selectedType] : undefined}
                                    onClose={() => setTypePickerVisible(false)}
                                    onConfirm={(value) => {
                                        // value[0] 是选中的对象 { label: '国际化文本', value: 'Gate' }
                                        // 提取 value 字段的值（原始枚举值）
                                        const selectedValue = value && value[0] 
                                            ? (typeof value[0] === 'string' 
                                                ? value[0] 
                                                : (value[0] as any)?.value || value[0])
                                            : null;
                                        setSelectedType(selectedValue);
                                        form.setFieldsValue({ type: selectedValue });
                                        setTypePickerVisible(false);
                                    }}
                                />
                            </Form.Item>
                        ) : (
                            // 更新模式：显示类型（只读）
                            <>
                                <Form.Item
                                    label={t('pages.delivery.orderDetail.type')}
                                >
                                    <div className="text-gray-700">
                                        {code?.type ? getLocalizedAccessCodeTypeName(code.type, t) : code?.type}
                                    </div>
                                </Form.Item>
                                
                                {/* Current Code (read-only) */}
                                <Form.Item
                                    label={t('pages.delivery.orderDetail.currentCode')}
                                >
                                    <div className="text-gray-700">{code?.code}</div>
                                </Form.Item>
                            </>
                        )}

                        {/* New Code (required) */}
                        <Form.Item
                            name="code"
                            label={t('pages.delivery.orderDetail.newCode')}
                            rules={[
                                { required: true, message: t('pages.delivery.orderDetail.codeRequired') },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.resolve();
                                        }
                                        const validation = validateAccessCodeFormat(value, t);
                                        if (validation.valid) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(validation.message));
                                    }
                                }
                            ]}
                        >
                            <Input
                                placeholder={t('pages.delivery.orderDetail.enterCode')}
                                clearable
                                maxLength={8}
                            />
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </div>
    );
};

export default AccessCodeFormView;

